import React from 'react';
import { CA_REPORT } from './report.type';
import { Division, LineItem } from '../glossary/glossary.type';
import { Report } from './Report';
import {
  getReport,
  updateReport as updateReportApi,
  bulkDeleteReportLineItems,
  bulkUpdateReportLineItem,
  addOrUpdateAdjustment,
} from './report.api';
import { toast } from 'react-toastify';
import { updateLineItem } from '../glossary/glossary.api';
import { handleError } from '../../common/utils/util.errors';
import { deepCopy } from '../../common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { useQueryClient } from 'react-query';

interface ReportWrapperProps {
  initialReport: CA_REPORT;
  divisions: Division[];
}

export const ReportWrapper: React.FC<ReportWrapperProps> = ({ initialReport, divisions }) => {
  const [report, setReport] = React.useState<CA_REPORT>(deepCopy(initialReport));
  const [isReportDirty, setIsReportDirty] = React.useState(false);
  const [isReportLoading, setIsReportLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const queryClient = useQueryClient();

  // Use a ref to store the current report state to avoid closure issues
  const currentReportRef = React.useRef<CA_REPORT>(deepCopy(initialReport));

  React.useEffect(() => {
    setReport(deepCopy(initialReport));
    currentReportRef.current = deepCopy(initialReport);
    setIsReportDirty(false);
  }, [initialReport]);

  const updateReport = (updateFn: (prevReport: CA_REPORT) => CA_REPORT) => {
    setReport(prevReport => {
      const newReport = updateFn(prevReport);
      currentReportRef.current = newReport;
      return newReport;
    });

    setIsReportDirty(true);
  };

  const resetReport = React.useCallback(() => {
    setReport(deepCopy(initialReport));
    currentReportRef.current = deepCopy(initialReport);
    setIsReportDirty(false);
  }, [initialReport]);

  const saveReport = async (optionalReportFields?: Partial<CA_REPORT>) => {
    try {
      setIsSaving(true);
      setIsReportLoading(true);

      // Use the ref to get the most current report state
      const reportToSave = currentReportRef.current;
      await updateReportApi(reportToSave.id, { generalInfoDto: reportToSave.generalInfo, ...reportToSave, ...optionalReportFields });
      const pendingUpdates: Promise<any>[] = [];
      const deletedLineItems = reportToSave.lineItems.filter(lineItem => lineItem?.isDeleted);
      if (deletedLineItems.length > 0) {
        pendingUpdates.push(bulkDeleteReportLineItems(deletedLineItems.map(lineItem => lineItem.id)));
      }
      const editedLineItems = reportToSave.lineItems.filter(lineItem => lineItem?.isEdited);
      if (editedLineItems.length > 0) {
        pendingUpdates.push(
          bulkUpdateReportLineItem({
            updatedReportLineItems: editedLineItems.map(li => {
              return { ...li, divisionId: li.division?.id ?? null, lineItemId: li.id };
            }),
          }),
        );
      }
      editedLineItems.forEach(lineItem => {
        if (lineItem.updateDivisionGlobally) {
          const newGlobalLineItem = {
            ...lineItem.lineItem,
            divisionId: lineItem.division?.id ?? null,
          } as LineItem;
          pendingUpdates.push(updateLineItem(lineItem.lineItem.id, newGlobalLineItem));
        }
      });
      await Promise.all(pendingUpdates);
      await addOrUpdateAdjustment(reportToSave.id);
      toast.success('All Changes Saved');
      setIsReportDirty(false);

      const updatedReport = await getReport(reportToSave.id);
      setReport(updatedReport);
      currentReportRef.current = updatedReport;
      queryClient.invalidateQueries([keys.caReport, reportToSave.id]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
      setIsReportLoading(false);
    }
  };

  const refetchReport = React.useCallback(async () => {
    try {
      setIsReportLoading(true);
      await queryClient.invalidateQueries([keys.caReport, report.id]);
      setIsReportDirty(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsReportLoading(false);
    }
  }, [report.id]);

  return (
    <Report
      report={report}
      updateReport={updateReport}
      isReportDirty={isReportDirty}
      isReportLoading={isReportLoading}
      divisions={divisions}
      saveReport={saveReport}
      undoChanges={resetReport}
      refetchReport={refetchReport}
      isSaving={isSaving}
    />
  );
};
